/**
 * API client для backend (Phase 4 day 2).
 *
 * Native fetch + thin wrapper. Header X-MAX-Init-Data из getMaxBridge().initData
 * (используем существующий wrapper, не window.MAX напрямую — иначе сломаем
 * dev mock).
 *
 * Endpoint base из import.meta.env.VITE_API_URL.
 *
 * Mock layer: если import.meta.env.DEV && import.meta.env.VITE_USE_MOCK==='true' —
 * lazy import api-mock.ts. Production — реальный fetch.
 *
 * Errors мапятся на ApiClientError с kind:
 *  - 'validation'              — 400 от Zod / phone (показать inline в форме).
 *  - 'auth'                    — 401 (initData невалидный, обновить страницу).
 *  - 'rate_limit'              — 429 (показать «слишком много попыток»).
 *  - 'temporarily_unavailable' — 503 + body.error='TEMPORARILY_UNAVAILABLE'
 *                                 (kill switch / idempotency lookup fail).
 *                                 Inline-redirect на /error, НЕ outbox.
 *  - 'network'                 — нет ответа от сервера (outbox path).
 *  - 'server'                  — прочее 5xx (outbox path).
 */

import { getMaxBridge } from './max-bridge';

export interface CreateLeadRequest {
  child_name: string;
  child_age: number;
  parent_name: string;
  parent_phone: string;
  consents: {
    pd_consent: true;
    pd_consent_version: string;
    pd_consent_accepted_at: string;
    privacy_policy_version: string;
    marketing_consent: boolean;
    marketing_consent_version: string;
    marketing_consent_at: string | null;
  };
  session_id: string;
  top_directions: string[];
  skill_level: string;
  raw_answers: Record<string, unknown>;
  /**
   * Готовый текст результата теста для alfaCRM `note` (Phase A P0 PR B).
   * Optional — если frontend не смог composер'нуть (lazy-import упал /
   * legacy state corruption), backend INSERT NULL + worker.ts fallback
   * на минимальный note.
   */
  result_summary?: string;
}

export interface CreateLeadResponse {
  lead_id: string;
  created_at: string;
}

export type ApiError =
  | { kind: 'validation'; details: unknown }
  | { kind: 'auth'; code?: string }
  | { kind: 'rate_limit'; retry_after_sec: number }
  | { kind: 'temporarily_unavailable'; code?: string }
  | { kind: 'network' }
  | { kind: 'server'; status: number };

export class ApiClientError extends Error {
  constructor(public readonly payload: ApiError) {
    super(payload.kind);
    this.name = 'ApiClientError';
  }
}

const API_BASE = import.meta.env.VITE_API_URL ?? '';
const USE_MOCK =
  import.meta.env.DEV === true && import.meta.env.VITE_USE_MOCK === 'true';

export async function createLead(req: CreateLeadRequest): Promise<CreateLeadResponse> {
  if (USE_MOCK) {
    const { mockCreateLead } = await import('./api-mock');
    return mockCreateLead(req);
  }

  const initData = getMaxBridge().initData;

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-MAX-Init-Data': initData,
      },
      body: JSON.stringify(req),
    });
  } catch {
    throw new ApiClientError({ kind: 'network' });
  }

  if (response.status === 201 || response.status === 200) {
    return (await response.json()) as CreateLeadResponse;
  }

  // Парсим body — может быть невалидный JSON, толерантный fallback.
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (response.status === 400) {
    const bodyObj = (body ?? {}) as { error?: string; details?: unknown };
    throw new ApiClientError({
      kind: 'validation',
      details: bodyObj.details ?? bodyObj.error ?? null,
    });
  }
  if (response.status === 401) {
    const bodyObj = (body ?? {}) as { code?: string };
    throw new ApiClientError({ kind: 'auth', code: bodyObj.code });
  }
  if (response.status === 429) {
    const bodyObj = (body ?? {}) as { retry_after_sec?: number };
    throw new ApiClientError({
      kind: 'rate_limit',
      retry_after_sec: bodyObj.retry_after_sec ?? 60,
    });
  }
  // 503 с конкретным body.error — kill switch или idempotency lookup fail.
  // Frontend показывает один экран по error, code только для backend logs.
  if (response.status === 503) {
    const bodyObj = (body ?? {}) as { error?: string; code?: string };
    if (bodyObj.error === 'TEMPORARILY_UNAVAILABLE') {
      throw new ApiClientError({
        kind: 'temporarily_unavailable',
        code: bodyObj.code,
      });
    }
  }
  // 5xx и прочее → server error.
  throw new ApiClientError({ kind: 'server', status: response.status });
}
